/**
 * Represents a TopBand component that detects scroll direction.
 * It hides the element when scrolling down and shows it when scrolling up.
 */
export default class TopBand {
    private element: HTMLElement;
    private parentElement: HTMLElement;
    private lastScrollY: number;
    private collapseDuration: string;

    constructor(element: HTMLElement) {
        if (element.dataset.siteId !== "Decades") return;

        this.element = element;
        this.parentElement = element.parentElement as HTMLElement;
        this.collapseDuration = getComputedStyle(document.documentElement).getPropertyValue('--collapse-duration').trim();
        this.lastScrollY = window.scrollY;
        this.initScrollListener();
    }

    /**
     * Initializes a scroll event listener to monitor the scroll direction.
     * The listener will call the `detectScrollDirection` method when the window is scrolled.
     */
    private initScrollListener() {
        window.addEventListener('scroll', this.detectScrollDirection);
    }

    /**
     * Detects the scroll direction and triggers the onScrollDirectionChange method if the scroll position has changed.
     */
    private detectScrollDirection = () => {
        const currentScrollY = window.scrollY;

        if (currentScrollY !== this.lastScrollY) {
            this.onScrollPositionChange(currentScrollY);
            this.lastScrollY = currentScrollY;
        }
    }

    /**
     * Handles the scroll event by adding or removing the is-hidden class based on the scroll position.
     * @param currentScrollY - The current vertical scroll position.
     */
    private onScrollPositionChange(currentScrollY: number) {
        if (currentScrollY > 0) {
            this.handleScrollDown();
        } else {
            this.handleScrollUp();
        }
    }

    /**
     * Hides the element when scrolling down or when the scroll position is not at the top.
     */
    private handleScrollDown() {
        this.parentElement.classList.add('collapsed');
        setTimeout(() => {
            this.element.setAttribute('aria-hidden', 'false');
            this.setTabIndexes(-1);

        }, parseFloat(this.collapseDuration) * 1000);
    }

    /**
     * Shows the element when scrolling up or when the scroll position is at the top.
     */
    private handleScrollUp() {
        this.parentElement.classList.remove('collapsed');
        this.setTabIndexes(0);
        this.element.setAttribute('aria-hidden', 'false');
    }

    /**
     * Sets the tabindex attribute on all links within the element.
     * @param value - The value to set for the tabindex attribute.
     */
    private setTabIndexes(value: number) {
        const links = this.element.querySelectorAll('a');
        links.forEach(link => {
            link.setAttribute('tabindex', value.toString());
        });
    }
}